import type { HttpClient } from './http';

export interface CustomModelFileRef {
  id: string;
  name?: string;
}

export interface CustomModelListItem {
  id: string;
  name: string;
  description: string | null;
  baseModel: string;
  prompt: string;
  temperature: number | null;
  reasoningLevel: 'fast' | 'balanced' | 'thorough';
  maxTokens: number | null;
  ownerUserId: string;
  organizationId: string | null;
  files: CustomModelFileRef[] | null;
  activeVersionNumber: number | null;
  latestVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomModelVersionDto {
  version: number;
  createdAt: string;
}

export interface ActiveChatPreview {
  revisionLabel: string;
  baseModel: string;
  prompt: string;
  temperature: number | null;
  reasoningLevel: 'fast' | 'balanced' | 'thorough';
  maxTokens: number | null;
  files: CustomModelFileRef[] | null;
}

export interface CustomModelDetail extends CustomModelListItem {
  versions: CustomModelVersionDto[];
  activeChatPreview: ActiveChatPreview;
}

export interface MyOrganization {
  id: string;
  name: string;
}

export interface CustomModelsApi {
  list(): Promise<CustomModelListItem[]>;
  get(id: string): Promise<CustomModelDetail>;
  create(body: {
    name: string;
    description?: string | null;
    baseModel: string;
    prompt: string;
    temperature?: number | null;
    reasoningLevel?: 'fast' | 'balanced' | 'thorough';
    maxTokens?: number | null;
    organizationId?: string | null;
    fileRefsToKeep?: CustomModelFileRef[];
    newFiles?: Array<{ name: string; content: string }>;
  }): Promise<CustomModelDetail>;
  update(
    id: string,
    body: {
      name?: string;
      description?: string | null;
      baseModel?: string;
      prompt?: string;
      temperature?: number | null;
      reasoningLevel?: 'fast' | 'balanced' | 'thorough';
      maxTokens?: number | null;
      organizationId?: string | null;
      fileRefsToKeep?: CustomModelFileRef[];
      newFiles?: Array<{ name: string; content: string }>;
      activeVersionNumber?: number | null;
    }
  ): Promise<CustomModelDetail>;
  setActiveVersionOnly(
    id: string,
    activeVersionNumber: number | null
  ): Promise<CustomModelDetail>;
  delete(id: string): Promise<void>;
  getAllowedExtensions(): Promise<{ extensions: string[] }>;
  preview(body: {
    prompt?: string;
    baseModel: string;
    instructions: string;
    temperature?: number | null;
    reasoningLevel?: 'fast' | 'balanced' | 'thorough';
    maxTokens?: number | null;
    fileIds?: string[];
  }): Promise<{ text: string }>;
  myOrganizations(): Promise<MyOrganization[]>;
}

const BASE = '/custom-models';

export function createCustomModelsApi(http: HttpClient): CustomModelsApi {
  return {
    list: () => http.get<CustomModelListItem[]>(BASE),
    get: (id: string) =>
      http.get<CustomModelDetail>(`${BASE}/${encodeURIComponent(id)}`),
    create: (body) => http.post<CustomModelDetail>(BASE, body),
    update: (id: string, body) =>
      http.patch<CustomModelDetail>(`${BASE}/${encodeURIComponent(id)}`, body),
    setActiveVersionOnly: (id, activeVersionNumber) =>
      http.patch<CustomModelDetail>(`${BASE}/${encodeURIComponent(id)}`, {
        activeVersionNumber,
      }),
    delete: (id: string) => http.delete(`${BASE}/${encodeURIComponent(id)}`),
    getAllowedExtensions: () =>
      http.get<{ extensions: string[] }>(`${BASE}/allowed-extensions`),
    preview: (body) => http.post<{ text: string }>(`${BASE}/preview`, body),
    myOrganizations: () =>
      http.get<MyOrganization[]>(`${BASE}/my-organizations`),
  };
}
